import JsBarcode from 'jsbarcode';
import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { PresenterProps } from 'src/components/PickingListComponent';
import { PickingList, ShipperList, UnloadList, ItemList, PICKING_PAGE_ROWS } from 'src/entities/pickingListEntity';

import style from './print.module.css';

function PickingHeader() {
  return (
    <>
      <div className={style.picking_title_wrapper}>
        <h1 className={style.picking_title}>ピッキングリスト</h1>
      </div>
      <div className={style.flex_area}>
        <div className={style.signature_area}>
          <div className={style.signature_box}>
            <div className={style.signature_header}>仕分け作業者</div>
            <div className={style.signature_body} />
          </div>
          <div className={style.signature_box}>
            <div className={style.signature_header}>検品者</div>
            <div className={style.signature_dual_body}>
              <div className={style.signature_dual_cell} />
              <div className={style.signature_dual_cell} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={style.delivery_row}>
      <div className={style.delivery_label}>{label}</div>
      <div className={style.delivery_value}>{value}</div>
    </div>
  );
}

function Barcode({ value }: { value: string }) {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !value) return;

    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        width: 1.6,
        height: 28,
        displayValue: true,
        fontSize: 9,
        margin: 0,
      });
    } catch {
      // バーコード化できない値でも帳票全体の表示は継続する。
    }
  }, [value]);

  return <svg ref={ref} className={style.barcode} data-testid="slip-barcode" />;
}

function getSlipNumbers(unload: UnloadList): string[] {
  return unload.slip_number_list?.length
    ? unload.slip_number_list
    : (unload.slip_numbers ?? '').split(',').map((slipNumber) => slipNumber.trim()).filter(Boolean);
}

function SlipNumberValue({ unload, barcode }: { unload: UnloadList; barcode: boolean }) {
  const slipNumbers = getSlipNumbers(unload);

  if (!barcode) return <>{unload.slip_numbers ?? ''}</>;

  return (
    <div className={style.barcode_list}>
      {slipNumbers.map((slipNumber) => (
        <Barcode value={slipNumber} key={slipNumber} />
      ))}
    </div>
  );
}

function DriverInfo({ unload, shipper, barcode }: { unload: UnloadList; shipper: ShipperList; barcode: boolean }) {
  return (
    <div className={style.delivery_info}>
      <div className={style.delivery_row_grid}>
        <InfoRow label="積込日" value={unload.items?.[0]?.loading_time_range_start ?? ''} />
        <InfoRow label="納品日" value={unload.items?.[0]?.unloading_time_range_start ?? ''} />
      </div>
      <InfoRow label="荷主名" value={shipper.shipper_name} />
      <InfoRow label="納品先名" value={unload.unloading_name} />
      <InfoRow label="納品先住所" value={unload.items?.[0]?.unloading_address ?? ''} />
      <InfoRow label="伝票No" value={<SlipNumberValue unload={unload} barcode={barcode} />} />
    </div>
  );
}

function GoodsTable({ displayItems, showSummary, summaryTotal }: { displayItems: ItemList[]; showSummary: boolean; summaryTotal: number }) {
  return (
    <table className={style.goods_table}>
      <thead>
        <tr className={style.header}>
          <th style={{ textWrap: 'nowrap' }}>商品CD</th>
          <th style={{ textWrap: 'nowrap' }}>商品名</th>
          <th style={{ textWrap: 'nowrap' }}>ロット</th>
          <th style={{ textWrap: 'nowrap' }}>数量</th>
          <th style={{ textWrap: 'nowrap' }}>荷姿</th>
          <th style={{ textWrap: 'nowrap' }}>備考</th>
        </tr>
      </thead>
      <tbody>
        {displayItems.map((row, index) => (
          <tr className={style.goods} key={`${index}-${row.item_code || 'empty'}-${row.empty_id ?? 'item'}`}>
            <td className={style.text_center}>{row.item_code}</td>
            <td className={style.text_left}>{row.item_name}</td>
            <td className={style.item_lot}>{row.item_lot_number ?? ''}</td>
            <td className={style.text_center}>{row.item_count !== 0 ? row.item_count : ''}</td>
            <td className={style.text_center}>{row.item_packing_style}</td>
            <td className={style.item_memo} aria-label="empty" />
          </tr>
        ))}
        {showSummary && (
          <tr className={style.summery_row}>
            <td className={style.summery} colSpan={3}>合計</td>
            <td className={style.summery}>{summaryTotal !== 0 ? summaryTotal : ''}</td>
            {/* 荷姿・備考の2列分。テーブル全6列の罫線を揃えるための空セル */}
            <td className={style.summery} colSpan={2} />
          </tr>
        )}
      </tbody>
    </table>
  );
}

function PickingFooter({
  courseNumber,
  driver,
  currentPage,
  totalPage,
}: {
  courseNumber?: number | null;
  driver: PickingList['driver'];
  currentPage: number;
  totalPage: number;
}) {
  return (
    <div className={style.footer}>
      {courseNumber != null && <div className={style.dispatch_box}>コース：{courseNumber}</div>}
      {driver.name && <div className={style.driver_box}>{driver.name}</div>}
      <div className={style.issue}>
        {`${formatDateTime(new Date())} 発行 `}
        {`<${currentPage}/${totalPage}>`}
      </div>
    </div>
  );
}

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

function makeEmptyRows(count: number): ItemList[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({
    item_carrier_company: '',
    item_code: '',
    item_count: 0,
    item_name: '',
    item_packing_style: '',
    loading_time_range_start: '',
    unloading_address: '',
    unloading_time_range_start: '',
    empty_id: i,
  }));
}

// 商品行を 20 行ごとに分割。最終/単一ページのみ 20 行までパディングする。
export function buildPickingPages(items: ItemList[]): ItemList[][] {
  if (items.length === 0) return [makeEmptyRows(PICKING_PAGE_ROWS)];
  const pages: ItemList[][] = [];
  for (let i = 0; i < items.length; i += PICKING_PAGE_ROWS) {
    const chunk = items.slice(i, i + PICKING_PAGE_ROWS);
    const isLast = i + PICKING_PAGE_ROWS >= items.length;
    pages.push(isLast ? [...chunk, ...makeEmptyRows(PICKING_PAGE_ROWS - chunk.length)] : chunk);
  }
  return pages;
}

// レンダリング用に展開済みの 1 ページ分の情報。
type PickingPage = {
  key: string;
  course: PickingList;
  shipper: ShipperList;
  unload: UnloadList;
  barcode: boolean;
  pageItems: ItemList[];
  isLast: boolean;
  summaryTotal: number;
};

// models をフラットなページ配列に展開する（純粋関数。レンダリング中の副作用を避けるため事前計算する）。
function buildPickingPageList(models: PickingList[], barcodeShipperSet: Set<string>): PickingPage[] {
  const list: PickingPage[] = [];
  // key は名称・車番が一意とは限らないため、ループのインデックスで一意性を保証する
  models.forEach((course, courseIdx) => {
    if (course.dispatches.length === 0) {
      list.push({
        key: `${courseIdx}-empty`,
        course,
        shipper: { shipper_name: '', unloads: [] },
        unload: { unloading_name: '', items: [] },
        barcode: false,
        pageItems: makeEmptyRows(PICKING_PAGE_ROWS),
        isLast: true,
        summaryTotal: 0,
      });
      return;
    }
    course.dispatches.forEach((loadDate, dispatchIdx) => {
      loadDate.shippers.forEach((shipper, shipperIdx) => {
        shipper.unloads.forEach((unload, unloadIdx) => {
          const items = unload.items ?? [];
          const barcode = barcodeShipperSet.has(shipper.shipper_name);
          const pages = buildPickingPages(items);
          const summaryTotal = items.reduce((s, it) => s + it.item_count, 0);
          pages.forEach((pageItems, pageIdx) => {
            list.push({
              key: `${courseIdx}-${dispatchIdx}-${shipperIdx}-${unloadIdx}-${pageIdx}`,
              course,
              shipper,
              unload,
              barcode,
              pageItems,
              isLast: pageIdx === pages.length - 1,
              summaryTotal,
            });
          });
        });
      });
    });
  });
  return list;
}

const PickingListPresenter: FC<PresenterProps> = memo(({ models, date: _date, unit: _unit, barcodeShippers }) => {
  const barcodeShipperSet = useMemo(() => new Set(barcodeShippers), [barcodeShippers]);
  const pages = useMemo(() => buildPickingPageList(models, barcodeShipperSet), [models, barcodeShipperSet]);
  const totalPage = pages.length;

  return (
    <>
      <Helmet>
        <title>ピッキングリスト</title>
      </Helmet>
      <div className={style.main}>
        {pages.map((page, index) => (
          <div className={style.page} style={{ page: 'A4PickingList' }} key={page.key}>
            <PickingHeader />
            <DriverInfo unload={page.unload} shipper={page.shipper} barcode={page.barcode} />
            <GoodsTable displayItems={page.pageItems} showSummary={page.isLast} summaryTotal={page.summaryTotal} />
            <PickingFooter courseNumber={page.course.course_number} driver={page.course.driver} currentPage={index + 1} totalPage={totalPage} />
          </div>
        ))}
      </div>
    </>
  );
});

export default PickingListPresenter;
